import React from "react";
import {
  Box,
  Autocomplete,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import TranscriptModeToggle, { TranscriptMode } from "./TranscriptModeToggle";
import { QuarterOption } from "../utils/quarterUtils";
import { hapticSelection } from "../../../../lib/haptics";

interface TranscriptControlsProps {
  quarterOptions: QuarterOption[];
  selectedQuarter: string;
  onQuarterSelect: (option: QuarterOption) => void;
  mode: TranscriptMode;
  onModeChange: (mode: TranscriptMode) => void;
  isLoading?: boolean;
}

const TranscriptControls: React.FC<TranscriptControlsProps> = ({
  quarterOptions,
  selectedQuarter,
  onQuarterSelect,
  mode,
  onModeChange,
  isLoading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: { xs: "center", sm: "flex-start" },
        justifyContent: { xs: "space-between", sm: "center" },
        gap: { xs: 1, sm: 2 },
        height: { xs: "auto", sm: "100%" },
      }}
    >
      <Autocomplete
        options={quarterOptions}
        getOptionLabel={(option) => option.label}
        value={quarterOptions.find((q) => q.value === selectedQuarter) ?? null}
        onChange={(_, newValue) => {
          if (newValue) {
            hapticSelection();
            onQuarterSelect(newValue);
          }
        }}
        disabled={isLoading}
        sx={{
          width: { xs: 130, sm: 160 },
          "& .MuiAutocomplete-clearIndicator": { display: "none" },
        }}
        ListboxProps={{
          sx: {
            maxHeight: 300,
            "& .MuiAutocomplete-option": {
              padding: 1.5,
              borderBottom:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.08)"
                  : "1px solid rgba(0, 0, 0, 0.08)",
              "&:last-child": {
                borderBottom: "none",
              },
              '&[aria-selected="true"]': {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(34, 197, 94, 0.1)"
                    : "rgba(34, 197, 94, 0.05)",
              },
              "&.Mui-focused": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0, 0, 0, 0.04)",
              },
            },
          },
        }}
        componentsProps={{
          paper: {
            sx: {
              background: theme.palette.mode === "dark" ? "#212121" : "#f5f5f5",
              border:
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.1)",
              borderRadius: 2,
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select quarter..."
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "transparent",
                borderRadius: 2,
                fontSize: "0.875rem",
                fontWeight: 500,
                height: "32px",
                "& .MuiAutocomplete-input": {
                  padding: "0 !important",
                },
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(0, 0, 0, 0.02)",
                },
                "&.Mui-focused": {
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.03)",
                },
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(0, 0, 0, 0.23)",
              },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(0, 0, 0, 0.4)",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#22c55e",
                borderWidth: "2px",
              },
              "& .MuiInputBase-input": {
                color: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
                "&::placeholder": {
                  color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  opacity: 1,
                },
              },
            }}
          />
        )}
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              padding: "8px 12px !important",
              gap: 2,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {option.label}
            </Typography>
            {option.isCurrentQuarter && (
              <Typography
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.25,
                  backgroundColor: "rgba(34, 197, 94, 0.15)",
                  color: "#22c55e",
                  borderRadius: 1,
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Current
              </Typography>
            )}
          </Box>
        )}
      />

      <TranscriptModeToggle
        mode={mode}
        onModeChange={onModeChange}
        disabled={isLoading}
        compact={isMobile}
      />
    </Box>
  );
};

export default TranscriptControls;
